import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const FractionPizza = () => {
  const navigate = useNavigate();
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [question, setQuestion] = useState(null);
  const [selectedSlices, setSelectedSlices] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [pizzasCompleted, setPizzasCompleted] = useState(0);

  const fractions = [
    { numerator: 1, denominator: 2, slices: 4 },
    { numerator: 1, denominator: 4, slices: 4 },
    { numerator: 3, denominator: 4, slices: 4 },
    { numerator: 1, denominator: 3, slices: 6 },
    { numerator: 2, denominator: 3, slices: 6 },
    { numerator: 1, denominator: 6, slices: 6 },
    { numerator: 5, denominator: 6, slices: 6 },
    { numerator: 2, denominator: 4, slices: 4 }, // equivalent to 1/2
  ];

  const generateQuestion = () => {
    const fraction = fractions[Math.floor(Math.random() * fractions.length)];
    const requiredSlices = Math.round((fraction.numerator / fraction.denominator) * fraction.slices);
    setQuestion({ ...fraction, requiredSlices });
    setSelectedSlices(0);
    setFeedback('');
  };

  useEffect(() => {
    generateQuestion();
  }, []);

  const handleSliceClick = () => {
    if (!question || selectedSlices >= question.slices) return;
    setSelectedSlices(selectedSlices + 1);
  };

  const handleSubmit = () => {
    if (!question) return;

    if (selectedSlices === question.requiredSlices) {
      setScore(score + 20);
      setPizzasCompleted(pizzasCompleted + 1);
      setFeedback('🎉 Perfect Pizza!');
      setTimeout(generateQuestion, 1500);
    } else {
      setFeedback(`❌ Try again! You need ${question.requiredSlices} slices for ${question.numerator}/${question.denominator}`);
      setTimeout(() => {
        setSelectedSlices(0);
        setFeedback('');
      }, 2000);
    }
  };

  const handleReset = () => {
    setSelectedSlices(0);
    setFeedback('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-400 via-red-400 to-pink-400 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 mb-6 text-white">
          <div className="flex justify-between items-center">
            <button onClick={() => navigate('/games')} className="text-white hover:text-yellow-300">
              ← Back
            </button>
            <h1 className="text-3xl font-bold">🍕 Fraction Pizza</h1>
            <div className="text-right">
              <div className="text-sm">Pizzas: {pizzasCompleted}</div>
              <div className="text-2xl font-bold">{score} pts</div>
            </div>
          </div>
        </div>

        {/* Game Area */}
        <div className="bg-white rounded-xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-2">
              Build a Pizza!
            </h2>
            {question && (
              <div className="text-2xl text-gray-600">
                Select <span className="font-bold text-orange-600">{question.numerator}/{question.denominator}</span> of the pizza
              </div>
            )}
          </div>

          {question && (
            <div className="max-w-md mx-auto">
              {/* Pizza Display */}
              <div className="relative w-64 h-64 mx-auto mb-8">
                <div className="absolute inset-0 bg-yellow-100 rounded-full border-8 border-yellow-600 overflow-hidden">
                  {Array.from({ length: question.slices }).map((_, index) => {
                    const angle = (360 / question.slices) * index;
                    const isSelected = index < selectedSlices;
                    return (
                      <div
                        key={index}
                        className={`absolute inset-0 origin-center transition-all cursor-pointer ${
                          isSelected ? 'opacity-100' : 'opacity-30'
                        }`}
                        style={{
                          transform: `rotate(${angle}deg)`,
                          clipPath: `polygon(50% 50%, 50% 0%, ${50 + 50 * Math.sin((Math.PI * 2) / question.slices)}% ${50 - 50 * Math.cos((Math.PI * 2) / question.slices)}%)`
                        }}
                        onClick={handleSliceClick}
                      >
                        <div className={`w-full h-full ${isSelected ? 'bg-red-500' : 'bg-gray-300'}`}>
                          <div className="absolute top-1/4 left-1/2 transform -translate-x-1/2">
                            {isSelected && '🍕'}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Info */}
              <div className="text-center mb-6">
                <div className="text-xl font-bold text-gray-700">
                  Selected: {selectedSlices} / {question.slices} slices
                </div>
                <div className="text-sm text-gray-500 mt-2">
                  Click the pizza to add slices
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-4">
                <button
                  onClick={handleReset}
                  className="flex-1 bg-gray-500 text-white text-lg font-bold py-3 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Reset
                </button>
                <button
                  onClick={handleSubmit}
                  className="flex-1 bg-orange-600 text-white text-lg font-bold py-3 rounded-lg hover:bg-orange-700 transition-colors"
                  disabled={selectedSlices === 0}
                >
                  Check Answer
                </button>
              </div>

              {feedback && (
                <div className={`mt-6 text-center text-xl font-bold ${feedback.includes('🎉') ? 'text-green-600' : 'text-red-600'}`}>
                  {feedback}
                </div>
              )}
            </div>
          )}

          {/* Help Section */}
          <div className="mt-8 p-4 bg-blue-50 rounded-lg">
            <h3 className="font-bold text-gray-800 mb-2">💡 How to Play:</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Click on the pizza to select slices</li>
              <li>• Select the correct number of slices for the fraction shown</li>
              <li>• For example: 1/2 means select half of the slices</li>
              <li>• Click "Reset" to start over, "Check Answer" to submit</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FractionPizza;